// §D9 — Returns widget for the MAIN /dashboard Overview. Same analytics
// endpoints as the dedicated /returns/dashboard page. The caller gates it on
// RETURN_ANALYTICS_VIEW (BE requires the same code) and shows a DeniedCard
// otherwise. The figures cover the last 30 days (BE default, no date sent).
import { Anchor, Box, Group, Text } from "@mantine/core";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnAnalyticsApi } from "../../../apis";
import { formatDuration } from "../../../utils/format-duration";
import classes from "./returns-widget.module.css";

const DASH = "-";

export function ReturnsDashboardWidget() {
  const { t } = useTranslation(["returns", "dashboard"]);
  // Real aggregates; 30s auto-refresh (same cadence as /returns/dashboard).
  const { data: dash } = useSWR(
    ["returns.dashboard.analytics"],
    () => ReturnAnalyticsApi.dashboard(),
    { refreshInterval: 30_000, revalidateOnFocus: false }
  );
  const { data: products } = useSWR(
    ["returns.top-products", undefined, undefined, 3],
    () => ReturnAnalyticsApi.topProducts({ limit: 3 }),
    { revalidateOnFocus: false }
  );
  const { data: buyers } = useSWR(
    ["returns.top-buyers", undefined, undefined, 3],
    () => ReturnAnalyticsApi.repeatReturners({ threshold: 1, limit: 3 }),
    { revalidateOnFocus: false }
  );

  const counts = dash?.counts;
  const money = dash?.money;
  const w = (k: string) => t(`dashboard:returnsWidget.${k}`);

  const tiles = [
    { label: w("active"), value: counts?.active ?? DASH, hint: w("activeHint") },
    { label: w("staffPending"), value: counts?.staffPending ?? DASH, hint: w("staffPendingHint") },
    { label: w("returnRate"), value: dash ? `${(dash.rates.returnRate * 100).toFixed(1)}%` : DASH, hint: w("returnRateHint") },
    { label: w("totalRefund"), value: money ? `${money.totalRefunded.toLocaleString("mn-MN")}₮` : DASH, hint: w("totalRefundHint") },
    { label: w("avgResolve"), value: dash ? formatDuration(dash.avgResolutionSeconds) : DASH, hint: w("avgResolveHint") },
    { label: w("resolved"), value: counts?.completed ?? DASH, hint: w("resolvedHint") },
  ];
  const productRows = products?.products ?? [];
  const buyerRows = buyers?.buyers ?? [];

  return (
    <Box className={classes.card}>
      <Box className={classes.head}>
        <Box>
          <Group gap={8} wrap="nowrap">
            <ArrowCounterClockwiseIcon size={20} />
            <Text className={classes.title}>{t("returns:dashboard.widget.title")}</Text>
          </Group>
          <Text className={classes.sub}>{w("period")}</Text>
        </Box>
        <Anchor component={Link} to="/returns/list" className={classes.link}>{w("listLink")}</Anchor>
      </Box>

      <Box className={classes.tiles}>
        {tiles.map((k) => (
          <Box key={k.label} className={classes.tile}>
            <Text className={classes.tileLabel}>{k.label}</Text>
            <Text className={classes.tileValue}>{k.value}</Text>
            <Text className={classes.tileHint}>{k.hint}</Text>
          </Box>
        ))}
      </Box>

      <Box className={classes.lists}>
        <Box className={classes.list}>
          <Text className={classes.listTitle}>{t("returns:dashboard.topProducts.title")}</Text>
          {productRows.length === 0 ? (
            <Box className={classes.listRow}>{DASH}</Box>
          ) : (
            productRows.map((p) => (
              <Box key={p.productId ?? p.productName} className={classes.listRow}>
                <Text fz={14} lineClamp={1}>{p.productName}</Text>
                <Text className={classes.listValue}>{p.returnCount} {t("dashboard:numberFormat.unit")}</Text>
              </Box>
            ))
          )}
        </Box>
        <Box className={classes.list}>
          <Text className={classes.listTitle}>{t("returns:dashboard.topBuyers.title")}</Text>
          {buyerRows.length === 0 ? (
            <Box className={classes.listRow}>{DASH}</Box>
          ) : (
            buyerRows.map((b) => (
              <Box key={b.buyerBusinessId} className={classes.listRow}>
                <Text fz={14} lineClamp={1}>{b.buyerBusinessName ?? DASH}</Text>
                <Text className={classes.listValue}>
                  {b.returnCount} {w("returnUnit")}
                  <Text component="span" className={classes.listMeta}> · {b.totalRefundAmount.toLocaleString("mn-MN")}₮</Text>
                </Text>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}
